import { useEffect, useState } from "react";
import NxtbnDrawer from "../drawer";
import { InputField, NXForm } from "../common";
import useApi from "../../api";
import { toast } from "react-toastify";
import { useLazyQuery } from "@apollo/client";
import { gql, useMutation } from "@apollo/client";
import { NXPen, NXUser } from "../../icons";

const GET_INVOICE = gql`
query GetInvoice($invoiceSettingsId: ID!) {
  invoiceSetting(invoiceSettingsId: $invoiceSettingsId) {
    city
    contactEmail
    contactPhone
    country
    isDefault
    postalCode
    storeAddress
    storeName
    logo
  }
}
`

const CREATE_INVOICE = gql`
mutation CreateInvoiceSettings($input: InvoiceSettingsInput!) {
  createInvoiceSettings(input: $input) {
    invoiceSettings {
      storeName
    }
  }
}
`


interface InvoiceSettingFormProps {
  isOpen: boolean;
  onClose: () => void;
  refresh: () => void;
  id: string;
}

function InvoiceSettingForm({ isOpen, onClose, refresh, id }: InvoiceSettingFormProps) {
  const [formData, setFormData] = useState<any>({
    store_name: "",
    store_address: "",
    city: "",
    country: "",
    postal_code: "",
    contact_email: "",
    is_default: false,
  });
  const [errorData, setErrorData] = useState<any>();
  const [image, setImage] = useState<File | null>();
  

  const [fetchInvoice, { data, loading, error }] = useLazyQuery(GET_INVOICE);
  const [createInvoice, { loading: createLoading, error: createError }] = useMutation(CREATE_INVOICE);

  const api = useApi();


  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev: any) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  
  const onSubmit = async (e: any) => {
    e.preventDefault();

    if (id) {
      api.updateInvoiceSettings(formData, id)
        .then(() => {
          toast.success("Invoice Settings Updated Successfully");
        })
        .catch(() => {
          toast.error("Failed to update invoice settings");
        });
    } else {
      try {
        const { data } = await createInvoice({
          variables: {
            input: {
              storeName: formData.store_name,
              storeAddress: formData.store_address,
              city: formData.city,
              country: formData.country,
              postalCode: formData.postal_code,
              contactEmail: formData.contact_email,
              contactPhone: formData.contact_phone,
              isDefault: formData.is_default,
            },
          },
        });

        toast.success("Invoice Settings Created Successfully");
        refresh();
        onClose();
      } catch (error) {
        toast.error("Failed to create invoice settings");
      }
    }
  };


  useEffect(() => {

    if (id) {
      fetchInvoice({ variables: { invoiceSettingsId: id } })
        .then((response) => {
            const formattedData = {
            store_name: response.data.invoiceSetting.storeName,
            store_address: response.data.invoiceSetting.storeAddress,
            city: response.data.invoiceSetting.city,
            country: response.data.invoiceSetting.country,
            postal_code: response.data.invoiceSetting.postalCode,
            contact_email: response.data.invoiceSetting.contactEmail,
            contact_phone: response.data.invoiceSetting.contactPhone,
            is_default: response.data.invoiceSetting.isDefault,
            logo: response.data.invoiceSetting.logo,
            };
            setFormData(formattedData);
        })
        .catch((error) => {
          console.error(error);
        });
    }

  }, [id]);

  const onChangeImage = (e: any) => {
    const file = e.target.files[0];
    setImage(file);
    setFormData((prev: any) => ({ ...prev, logo: file }));

    const imagePayload = {
      logo: file,
    }

    api.uploadInvoiceLogo(imagePayload, id).then((response) => {
        toast.success("Logo Uploaded Successfully");
      },
      (error) => {
        toast.error("Failed to upload logo");
      }
    );
  };


  return (
    <NxtbnDrawer isOpen={isOpen} onClose={onClose} title={id ? "Edit Invoice Settings" : "Add Invoice Settings"}>
      <NXForm className="p-4" onSubmit={onSubmit} title="" nonFieldError={{}}>

        {id && <div className="flex justify-center mb-4">
          <div className="h-[200px] w-[200px] rounded-full relative">
            {formData?.logo || image ? 
          <img
            src={`${image ? URL.createObjectURL(image) : formData.logo}`}
            alt=""
            className="h-[200px] w-[200px] rounded-full border border-base-200 object-cover"
          />
          :
          <NXUser className="w-[150px] h-[150px]"/>
              }
            <label
              htmlFor="change_image"
              className="border border-base-200 absolute bottom-6 right-1 w-8 h-8 rounded-full bg-primary-500 flex justify-center items-center p-2 cursor-pointer"
            >
              <NXPen className="h-6 w-6 z-50 stroke-white" />
            </label>
            <InputField
              id="change_image"
              type="file"
              className="hidden"
              onChange={(e) => onChangeImage(e)}
            />
          </div>
        </div>}

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">          
          <div>
            <label className="block text-sm font-medium text-gray-700">Store Name</label>
            <InputField
              type="text"
              name="store_name"
              placeholder="Your Store"
              value={formData.store_name}
              onChange={handleInputChange}
              required
              className="mt-1 w-full p-2 border rounded"
              errorData={errorData}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700">Store Address</label>
            <InputField
              type="text"
              name="store_address"
              placeholder="Store Address"
              value={formData.store_address}
              onChange={handleInputChange}
              required
              className="mt-1 w-full p-2 border rounded"
              errorData={errorData}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700">City</label>
            <InputField
              type="text"
              name="city"
              placeholder="City"
              value={formData.city}
              onChange={handleInputChange}
              required
              className="mt-1 w-full p-2 border rounded"
              errorData={errorData}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700">Country</label>
            <InputField
              type="text"
              name="country"
              placeholder="Country"
              value={formData.country}
              onChange={handleInputChange}
              required
              className="mt-1 w-full p-2 border rounded"
              errorData={errorData}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700">Postal Code</label>
            <InputField
              type="text"
              name="postal_code"
              placeholder="123456"
              value={formData.postal_code}
              onChange={handleInputChange}
              required
              className="mt-1 w-full p-2 border rounded"
              errorData={errorData}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700">Contact Email</label>
            <InputField
              type="email"
              name="contact_email"
              placeholder="invoice@example.com"
              value={formData.contact_email}
              onChange={handleInputChange}
              required
              className="mt-1 w-full p-2 border rounded"
              errorData={errorData}
            />
          </div>
          <div className="flex items-center">
            <input
              type="checkbox"
              name="is_default"
              checked={formData.is_default}
              onChange={handleInputChange}
              className="mr-2"
            />
            <label className="text-sm font-medium text-gray-700">Set as Default</label>
          </div>
        </div>
        <div className="flex justify-end mt-4">
          <button
            type="submit"
            className="px-4 py-2 text-white bg-[#0CAF60] hover:bg-[#098d4d] disabled:bg-[#0caf609a] text-sm font-medium rounded-md shadow-sm focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
          >
            Save Settings
          </button>
        </div>
      </NXForm>
    </NxtbnDrawer>
  );
}

export default InvoiceSettingForm;
